import { useState, useEffect } from 'react'

// 🎛️ COCKPIT DES AGENTS — maquette (2026-08-25)
// L'onglet "stats" (inutile) est recyclé en annuaire vivant des agents.
// Principe : 1 agent = 1 carte (avatar + fiche) → clic = on ouvre l'agent EN GRAND
// (pas de split-panes, décision UX 25/08). Polymorphe : ttyd pour les CLI, chat iframe pour Kirajean.
// Sections par MACHINE (VPS / PC IA / PC FM) comme la page Projets. Chaque agent a son dossier (📁).
// Source à venir : lire les vraies fiches depuis les dossiers "agent-*" (domaine AGENTS = annuaire maître).

// ✅ 2026-08-25 : maquette validée bout-en-bout (terminal Claude ouvert en grand depuis le phone via Tailscale).
const TS = 'srv836585.tail3ea350.ts.net' // accès par le NOM (cert Let's Encrypt) — Tailscale requis

type Agent = {
  id: string
  name: string
  avatar: string      // emoji pour la maquette (DiceBear plus tard)
  role: string
  model: string
  kind: 'terminal' | 'chat' | 'link'
  url: string
  scheme: 'https' | 'http'
  folder: string      // dossier Claudie (annuaire maître) → bouton 📁
  accent: string
}

type Machine = { key: string; label: string; note?: string; agents: Agent[] }

const MACHINES: Machine[] = [
  {
    key: 'vps', label: '🖥️ VPS',
    // Costumes (Claude/Goose/Kirajean + tes agents dupliqués) = injectés DYNAMIQUEMENT
    // depuis /api/cockpit/personas (voir personaAgents). Ici : seulement le non-persona.
    agents: [
      { id: 'shell', name: 'Shell', avatar: '🐚', role: 'Accès système brut', model: '—', kind: 'terminal', scheme: 'https', url: `https://${TS}:7682/?arg=shell`, folder: 'agent-shell', accent: '#a855f7' },
      { id: 'n8n', name: 'n8n IA', avatar: '🔧', role: 'n8n + agent IA (ouvre l\'outil)', model: 'n8n-ia', kind: 'link', scheme: 'http', url: `http://${TS}:5679/`, folder: 'agent-n8n', accent: '#ea4b71' },
      { id: 'hermes', name: 'Hermes WebUI', avatar: '⚡', role: 'Agent Hermes (WebUI VPS) — ouvre l\'outil', model: 'hermes-webui', kind: 'link', scheme: 'http', url: `http://${TS}:8787/`, folder: 'hermes', accent: '#f59e0b' },
    ],
  },
  { key: 'pcia', label: '🏭 PC MEGAPORT', note: 'à venir — Hermes + sous-agents (usine, inférence locale)', agents: [] },
  { key: 'pcfm', label: '🪟 PC FM', note: 'portable france-metal (Tailscale) — sous-agents à venir', agents: [
      { id: 'hermes-pcfm', name: 'Hermes (PC FM)', avatar: '⚡', role: 'Agent Hermes (WebUI portable) — causer / voir config', model: 'hermes-webui', kind: 'link', scheme: 'http', url: `http://france-metal-pc-portable.tail3ea350.ts.net:8787/`, folder: 'agent-hermes', accent: '#f59e0b' },
    ] },
]

// Palette stable pour les tuiles de costumes (couleur dérivée du nom → constante par agent).
const PERSONA_ACCENTS = ['#ec4899', '#22c55e', '#3b82f6', '#f59e0b', '#a855f7', '#06b6d4', '#ef4444', '#14b8a6']
const colorFromName = (s: string) =>
  PERSONA_ACCENTS[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % PERSONA_ACCENTS.length]

// personaToAgent : un persona ajean (nom, avatar, maison) → une tuile costume du cockpit.
// URL = deep-link vers sa maison en mode costume (?conv=<home>&costume=1, nouvel onglet).
const personaToAgent = (p: { name: string; avatar: string; home: string }): Agent => ({
  id: 'persona-' + p.name,
  name: p.name,
  avatar: p.avatar || '🎭',
  role: 'Costume — chat',
  model: 'Kirajean',
  kind: 'chat',
  scheme: 'https',
  url: `https://${TS}:9090/?conv=${p.home}&costume=1`,
  folder: 'agent-' + p.name.toLowerCase(),
  accent: colorFromName(p.name),
})

interface StatsPageProps {
  onOpenProject?: (projectName: string) => void
}

export default function StatsPage({ onOpenProject }: StatsPageProps) {
  const [open, setOpen] = useState<Agent | null>(null)
  const [personaAgents, setPersonaAgents] = useState<Agent[]>([])
  const [busy, setBusy] = useState(false)

  // Échap ferme la vue plein écran
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Cockpit dynamique : les costumes (Claude/Goose/Kirajean + agents dupliqués) sont
  // lus depuis ajean via le proxy backend (/api/cockpit/personas). Créer un agent →
  // il apparaît ici tout seul. En cas d'échec, les tuiles statiques restent affichées.
  const loadPersonas = async () => {
    try {
      const r = await fetch('/api/cockpit/personas')
      const d = await r.json()
      if (d?.ok) setPersonaAgents((d.personas || []).filter((p: any) => p.home).map(personaToAgent))
    } catch { /* ajean injoignable → on garde les tuiles statiques */ }
  }
  useEffect(() => { loadPersonas() }, [])

  // Dupliquer un agent : clone le moule Goose (persona + maison) côté ajean, puis on
  // rafraîchit la liste → la nouvelle tuile apparaît sans rechargement de page.
  const duplicateAgent = async () => {
    const name = window.prompt('Nom du nouvel agent (dupliqué depuis Goose) :')
    if (!name || !name.trim()) return
    setBusy(true)
    try {
      const r = await fetch('/api/cockpit/duplicate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      })
      const d = await r.json()
      if (!r.ok || !d?.ok) { alert('Échec : ' + (d?.detail || d?.error || 'erreur')); return }
      await loadPersonas()
      alert('Agent « ' + name.trim() + ' » créé ✅ — retrouve-le dans les tuiles, puis personnalise-le via ✎ dans le costume.')
    } catch { alert('Erreur réseau (ajean injoignable ?)') }
    finally { setBusy(false) }
  }

  const openAgent = (a: Agent) => {
    // kind 'link' = raccourci vers un OUTIL externe (ex. n8n) → toujours nouvel onglet
    // (n8n ne tient pas en iframe tierce : cookies de session bloqués). http = idem
    // (mixed-content en iframe depuis le Hub https).
    // Costumes (URL costume=1) + outils externes + http → nouvel onglet plein écran,
    // JAMAIS en iframe embarqué (le costume emboîté gèle le tactile sur Android).
    if (a.kind === 'link' || a.scheme === 'http' || a.url.includes('costume=1')) {
      window.open(a.url, '_blank', 'noopener')
      return
    }
    setOpen(a)
  }

  return (
    <div style={{ padding: '24px', backgroundColor: '#111827', height: '100%', width: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#ec4899', marginBottom: '6px' }}>🎛️ Cockpit des agents</h1>
      <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '28px' }}>
        Annuaire vivant — clique un agent pour l'ouvrir <b>en grand</b>, ou 📁 pour entrer dans son dossier. Terminaux (ttyd) et chat Kirajean, deux rails indépendants.
      </p>

      <a
        href={`http://${TS}:3000/`}
        target="_blank"
        rel="noopener"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px', textDecoration: 'none',
          backgroundColor: '#1f2937', border: '1px solid #f59e0b', borderRadius: '10px',
          padding: '12px 18px', marginBottom: '28px', color: '#f3f4f6', fontWeight: 600,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2a2f3a' }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1f2937' }}
      >
        <span style={{ fontSize: '20px' }}>🛰️</span>
        <span>Mission Control — tour de contrôle (radar / tâches)</span>
        <span style={{ color: '#f59e0b', fontSize: '13px' }}>↗ ouvre dans un onglet</span>
      </a>

      <button
        onClick={duplicateAgent}
        disabled={busy}
        title="Créer un nouvel agent en dupliquant Goose (persona + maison). Il apparaît aussitôt dans les tuiles."
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px', marginLeft: '12px',
          backgroundColor: '#1f2937', border: '1px solid #22c55e', borderRadius: '10px',
          padding: '12px 18px', marginBottom: '28px', color: '#f3f4f6', fontWeight: 600,
          cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2a2f3a' }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1f2937' }}
      >
        <span style={{ fontSize: '20px' }}>➕</span>
        <span>{busy ? 'Création…' : 'Dupliquer un agent'}</span>
        <span style={{ color: '#22c55e', fontSize: '13px' }}>moule Goose</span>
      </button>

      {MACHINES.map((m) => {
        const agents = m.key === 'vps' ? [...personaAgents, ...m.agents] : m.agents
        return (
        <section key={m.key} style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', borderBottom: '1px solid #374151', paddingBottom: '8px', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#f3f4f6' }}>{m.label}</h2>
            <span style={{ color: '#6b7280', fontSize: '12px' }}>{agents.length} agent{agents.length > 1 ? 's' : ''}</span>
          </div>

          {agents.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: '13px', fontStyle: 'italic', padding: '8px 0' }}>{m.note}</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {agents.map((a) => (
                <div
                  key={a.id}
                  onClick={() => openAgent(a)}
                  style={{ cursor: 'pointer', backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '12px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px', transition: 'all 0.15s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = a.accent; e.currentTarget.style.transform = 'translateY(-2px)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#374151'; e.currentTarget.style.transform = 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', flexShrink: 0, backgroundColor: '#111827', border: `2px solid ${a.accent}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', overflow: 'hidden' }}>
                      {a.avatar.startsWith('http')
                        ? <img src={a.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : a.avatar}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: '#f3f4f6', fontSize: '17px', fontWeight: 700 }}>{a.name}</div>
                      <div style={{ color: '#9ca3af', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.role}</div>
                    </div>
                    <button
                      title="Ouvrir le dossier de l'agent"
                      onClick={(e) => { e.stopPropagation(); onOpenProject?.(a.folder) }}
                      style={{ flexShrink: 0, backgroundColor: '#111827', color: '#d1d5db', border: '1px solid #374151', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '13px' }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = a.accent }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#374151' }}
                    >📁</button>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '2px' }}>
                    <Tag>🧠 {a.model}</Tag>
                    <Tag>{a.kind === 'terminal' ? '⌨️ Terminal' : a.kind === 'link' ? '🔧 Outil' : '💬 Chat'}</Tag>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', display: 'inline-block' }} />
                    <span style={{ color: '#6b7280', fontSize: '11px' }}>
                      {a.kind === 'link' ? "ouvre l'outil dans un onglet" : a.scheme === 'http' ? "ouvre dans un onglet (front https à venir)" : "prêt — ouvrir en grand"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        )
      })}

      <p style={{ color: '#4b5563', fontSize: '12px', marginTop: '8px' }}>
        Maquette — fiches lues bientôt depuis les dossiers « agent-* » (annuaire maître) ; avatars DiceBear à brancher.
        Accès Tailscale requis (agents exposés sur le tailnet uniquement).
      </p>

      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: '#0b1220', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', backgroundColor: '#111827', borderBottom: `1px solid ${open.accent}` }}>
            <button onClick={() => setOpen(null)} style={{ color: '#ffffff', backgroundColor: open.accent, border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>← Retour à Claudie</button>
            <span style={{ fontSize: '22px', marginLeft: '4px' }}>{open.avatar}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#f3f4f6', fontWeight: 700 }}>{open.name}</div>
              <div style={{ color: '#9ca3af', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{open.role} · {open.model}</div>
            </div>
            <a href={open.url} target="_blank" rel="noopener" style={{ color: '#9ca3af', fontSize: '13px', textDecoration: 'none', padding: '6px 10px' }}>↗ Onglet</a>
            <span style={{ color: '#4b5563', fontSize: '11px' }}>Échap pour fermer</span>
          </div>
          <iframe title={open.name} src={open.url} style={{ flex: 1, width: '100%', border: 'none', backgroundColor: '#000' }} />
        </div>
      )}
    </div>
  )
}

function Tag({ children }: any) {
  return (
    <span style={{ backgroundColor: '#111827', color: '#d1d5db', fontSize: '11px', padding: '3px 8px', borderRadius: '6px', border: '1px solid #374151' }}>{children}</span>
  )
}
